import { markForcedUpdate } from './newVersion.ts';

/**
 * Installed once, before anything else makes a request (`main.tsx`): every
 * same-origin call this tab makes carries `X-App-Release`, this build's own
 * release time, and any 426 it gets back -- the proxy refusing a call below
 * the deploy's forced minimum (`Caddyfile`) -- marks the same forced-update
 * signal `/version.json`'s own poll gives, so a tab that is mid-sync notices
 * at its very next call rather than waiting for its next focus or
 * five-minute poll (agora#528).
 *
 * A cross-origin call -- another Agora's `.well-known` config, a presigned
 * upload URL -- is left exactly as it was: this origin's minimum says
 * nothing about a different one, and an extra header could break a signed
 * request.
 */
export function installReleaseGate(ownRelease: number, target: typeof window = window): void {
  const original = target.fetch.bind(target);
  target.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let url: URL;
    try {
      url = new URL(input instanceof Request ? input.url : input, target.location.href);
    } catch {
      return original(input, init);
    }
    if (url.origin !== target.location.origin) return original(input, init);

    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => {
        headers.set(key, value);
      });
    }
    headers.set('X-App-Release', String(ownRelease));

    const response = await original(input, { ...init, headers });
    if (response.status === 426) markForcedUpdate();
    return response;
  }) as typeof fetch;
}
